// Program Section Data - ADA Continental Political Alliance
// DO NOT MODIFY - Official content

export const programContent = {
  en: {
    // Program Landing Page
    landing: {
      hero: {
        title: "A Program for Africa's Political Union",
        subtitle: "Institutional transformation. Economic sovereignty. Social renewal.",
        primaryCta: "Read the Program",
        secondaryCta: "Explore ADA Branded Parties"
      },
      transformations: {
        title: "The Three Foundational Transformations",
        items: [
          {
            id: "institutional",
            title: "Institutional Transformation",
            summary: "Rebuilding democratic institutions with transparency, accountability, and citizen participation at their core. Establishing constitutional frameworks that serve African realities.",
            link: "/program/chapters#part-1"
          },
          {
            id: "economic",
            title: "Economic Transformation",
            summary: "Achieving economic sovereignty through territorial wealth creation, strategic industrialization, and continental integration. Ending extractive dependency.",
            link: "/program/chapters#part-3"
          },
          {
            id: "social",
            title: "Social Transformation",
            summary: "Investing in human capital through education, healthcare, and cultural renewal. Empowering youth and ensuring equitable access to opportunity.",
            link: "/program/chapters#part-5"
          }
        ]
      },
      continentalModel: {
        title: "ADA's Continental Model",
        description: "ADA operates as a continental political alliance composed of nationally registered parties sharing a common manifesto, charter, and institutional vision. This federated structure enables local adaptation while maintaining ideological coherence across borders."
      },
      architecture: {
        title: "Program Architecture",
        items: [
          {
            title: "Continental Manifesto",
            description: "The shared ideological foundation binding all ADA-branded parties across the continent."
          },
          {
            title: "National Program Adaptation",
            description: "Country-level translation of continental principles to address local realities and priorities."
          },
          {
            title: "Parliamentary Strategy",
            description: "Building toward an ADA bloc at the Pan-African Parliament to advance continental legislation."
          },
          {
            title: "Constitutional Convergence",
            description: "Long-term harmonization of constitutional frameworks toward African political union."
          }
        ]
      },
      navigation: {
        house: {
          title: "The House of the Alliance",
          subtitle: "Johannesburg Headquarters",
          description: "The political, intellectual, and strategic center of the continental movement."
        },
        cabinet: {
          title: "Shadow Cabinet",
          subtitle: "Government-in-Waiting",
          description: "A symbolic executive structure modeling Africa's future unified government."
        }
      }
    },

    // Branded Parties Page
    brandedParties: {
      hero: {
        title: "ADA Branded Parties",
        subtitle: "A federated political brand for continental unity"
      },
      definition: {
        title: "Definition",
        content: "ADA is a federated political brand: autonomous national parties operating under one continental manifesto and charter. Each party maintains legal independence while sharing a common vision, identity, and programmatic framework."
      },
      identity: {
        title: "Identity System",
        items: [
          {
            title: "Naming Convention",
            description: "ADA + country code (ADA-CI, ADA-SN, ADA-GH, ADA-NG, ADA-KE, ADA-ZA, etc.)"
          },
          {
            title: "Visual Identity",
            description: "Unified logo, symbols, and color palette across all national parties."
          },
          {
            title: "Program Alignment",
            description: "National programs adapted to local realities but aligned with the continental manifesto."
          },
          {
            title: "Charter Commitment",
            description: "Mandatory signature of the Charter of the Alliance by all founding members."
          }
        ]
      },
      cohesion: {
        title: "Programmatic Cohesion",
        intro: "All ADA-branded parties commit to three non-negotiable transformations:",
        items: [
          "Institutional Transformation — Democratic renewal and constitutional reform",
          "Economic Transformation — Sovereignty, industrialization, and territorial wealth",
          "Social Transformation — Human capital, equity, and cultural renaissance"
        ],
        note: "The shared manifesto serves as the ideological base. National translations follow a common structure while addressing country-specific challenges."
      },
      governance: {
        title: "Governance Model",
        bodies: [
          {
            name: "Continental Political Bureau (CPB)",
            role: "Strategic coordination across all ADA-branded parties"
          },
          {
            name: "Council of Elders",
            role: "Ethical guidance and historical continuity"
          },
          {
            name: "Constitutional Oversight Unit",
            role: "Legal harmonization and convergence"
          },
          {
            name: "National Executive Bureaus",
            role: "Country-level party leadership and operations"
          }
        ]
      },
      strategy: {
        title: "Electoral & Parliamentary Strategy",
        items: [
          "National elections contested by ADA-branded parties under local electoral laws",
          "Formation of an ADA Parliamentary Group at the Pan-African Parliament",
          "Long-term objective: transform the Pan-African Parliament into a legislative body with real power"
        ]
      },
      advantages: {
        title: "Strategic Advantages",
        items: [
          { title: "Unity of Identity", description: "One recognizable political brand across the continent" },
          { title: "National Adaptability", description: "Freedom to address local priorities within a shared framework" },
          { title: "Continental Coherence", description: "Aligned messaging and coordinated political action" },
          { title: "Long-term Scalability", description: "Structured expansion model for new countries" },
          { title: "Institutional Credibility", description: "Professional governance and transparent operations" }
        ]
      },
      cta: {
        title: "Launch an ADA Section in Your Country",
        disclaimer: "Submission does not imply affiliation. ADA reserves the right to approve, decline, or postpone any application.",
        form: {
          fullName: "Full Name",
          country: "Country",
          email: "Email Address",
          background: "Background / Experience",
          motivation: "Short Motivation (max 300 words)",
          submit: "Submit Application"
        }
      }
    },

    // House of the Alliance Page
    house: {
      hero: {
        title: "The House of the Alliance",
        subtitle: "Political, intellectual, and strategic headquarters",
        location: "Johannesburg, South Africa"
      },
      intro: {
        title: "Introduction",
        content: "The House of the Alliance is the political, intellectual, and strategic headquarters of ADA, strategically located in Johannesburg near the Pan-African Parliament. It serves as the operational center for continental coordination and the symbolic home of Africa's political renewal."
      },
      pillars: {
        title: "Core Functions",
        items: [
          {
            title: "Continental General Secretariat",
            description: "Central coordination of all ADA-branded parties and continental operations."
          },
          {
            title: "Pan-African Leadership & Governance Academy",
            description: "Training future political leaders in democratic governance, policy design, and institutional management."
          },
          {
            title: "Policy Think Tank & Research Center",
            description: "Evidence-based policy development and continental research on governance, economy, and society."
          },
          {
            title: "Continental Diplomatic Hub",
            description: "Facilitating dialogue between ADA parties, civil society, and international partners."
          },
          {
            title: "Living Archive of Pan-African Political Thought",
            description: "Preserving and advancing the intellectual heritage of African political philosophy."
          }
        ]
      },
      governance: {
        title: "Strategic Governance Framework",
        bodies: [
          {
            name: "Continental Political Bureau (CPB)",
            description: "Strategic coordination across all ADA-branded parties, setting continental priorities and ensuring alignment."
          },
          {
            name: "Council of Elders",
            description: "Ethical guidance, historical continuity, and institutional safeguards for the Alliance."
          },
          {
            name: "Constitutional Oversight Unit",
            description: "Legal harmonization and constitutional convergence work across member parties."
          }
        ]
      },
      whyJohannesburg: {
        title: "Why Johannesburg?",
        reasons: [
          "Proximity to Pan-African Parliament and continental institutions",
          "Major continental transport hub with connections across Africa",
          "Symbolic bridge between African regions — Southern gateway to the continent",
          "Established infrastructure for international political organizations"
        ]
      },
      cta: {
        title: "Partner with the House of the Alliance",
        contact: "Contact: [TBD]",
        description: "Academic institutions, civil society organizations, and policy research centers are invited to explore partnership opportunities."
      }
    },

    // Shadow Cabinet Page
    shadowCabinet: {
      hero: {
        title: "Shadow Cabinet",
        subtitle: "Government-in-Waiting for a United Africa"
      },
      intro: "To embody the vision of a politically united Africa, ADA establishes a symbolic Shadow Cabinet. This structure models the future executive of a united African political entity and supports policy preparation and coherence.",
      ministries: [
        { ministry: "Head of State (Union-level)", mandate: "Strategic leadership and representation of the African political union" },
        { ministry: "Foreign Affairs & Pan-African Diplomacy", mandate: "Continental diplomacy, international relations, and African voice on global stage" },
        { ministry: "Constitutional Affairs & Political Union", mandate: "Constitutional convergence, political integration, and institutional harmonization" },
        { ministry: "Economic Integration & Finance", mandate: "Continental economic policy, monetary coordination, and fiscal frameworks" },
        { ministry: "Digital Economy & Innovation", mandate: "Technology sovereignty, digital infrastructure, and innovation ecosystems" },
        { ministry: "Justice & Rule of Law", mandate: "Legal harmonization, human rights, and continental justice mechanisms" },
        { ministry: "Defense & Strategic Sovereignty", mandate: "Collective security, strategic autonomy, and defense cooperation" },
        { ministry: "Health & Social Solidarity", mandate: "Healthcare systems, pandemic preparedness, and social protection" },
        { ministry: "Education & Knowledge Systems", mandate: "Educational standards, research coordination, and knowledge exchange" },
        { ministry: "Agriculture & Food Sovereignty", mandate: "Food security, agricultural modernization, and rural development" },
        { ministry: "Infrastructure & Urban Development", mandate: "Continental connectivity, urbanization, and infrastructure investment" },
        { ministry: "Climate, Environment & Resilience", mandate: "Environmental protection, climate adaptation, and sustainable development" },
        { ministry: "Culture, Arts & African Identity", mandate: "Cultural preservation, creative industries, and African heritage" },
        { ministry: "Youth & Diaspora Affairs", mandate: "Youth empowerment, diaspora engagement, and intergenerational dialogue" },
        { ministry: "Women & Equity", mandate: "Gender equality, women's leadership, and inclusive governance" }
      ],
      footer: "This shadow structure is conceptual and evolves as ADA expands across countries and institutions."
    },

    // Program Chapters Page
    chapters: {
      hero: {
        title: "Program Chapters",
        subtitle: "The complete political program of the African Democratic Alliance"
      },
      intro: "The ADA program is structured in seven parts, each addressing critical dimensions of Africa's transformation. Together, they form a coherent roadmap for institutional, economic, and social renewal.",
      parts: [
        {
          number: 1,
          title: "Foundations of Institutional Transformation",
          transformation: "Institutional",
          content: "[Insert Part 1 text — Institutional Transformation: Democratic renewal, constitutional reform, separation of powers, and citizen participation]"
        },
        {
          number: 2,
          title: "Governance & Public Administration",
          transformation: "Institutional",
          content: "[Insert Part 2 text — Governance frameworks, public service reform, decentralization, and accountability mechanisms]"
        },
        {
          number: 3,
          title: "Economic Sovereignty & Territorial Wealth",
          transformation: "Economic",
          content: "[Insert Part 3 text — Economic transformation: territorial corporations, local production, value chains, and economic autonomy]"
        },
        {
          number: 4,
          title: "Continental Integration & Trade",
          transformation: "Economic",
          content: "[Insert Part 4 text — Regional integration, intra-African trade, monetary coordination, and continental markets]"
        },
        {
          number: 5,
          title: "Human Capital & Social Development",
          transformation: "Social",
          content: "[Insert Part 5 text — Education, healthcare, social protection, and human development priorities]"
        },
        {
          number: 6,
          title: "Youth, Culture & Identity",
          transformation: "Social",
          content: "[Insert Part 6 text — Youth empowerment, cultural renaissance, African identity, and diaspora engagement]"
        },
        {
          number: 7,
          title: "Vision 2063 & Political Union",
          transformation: "Institutional",
          content: "[Insert Part 7 text — Long-term vision, African political union, and constitutional convergence roadmap]"
        }
      ],
      transformationColors: {
        Institutional: "purple",
        Economic: "gold",
        Social: "teal"
      }
    },

    // Navigation
    nav: {
      program: "Program",
      overview: "Overview",
      brandedParties: "Branded Parties",
      house: "House of the Alliance",
      shadowCabinet: "Shadow Cabinet",
      chapters: "Program Chapters"
    }
  },

  fr: {
    // French translations - same structure
    landing: {
      hero: {
        title: "Un Programme pour l'Union Politique de l'Afrique",
        subtitle: "Transformation institutionnelle. Souveraineté économique. Renouveau social.",
        primaryCta: "Lire le Programme",
        secondaryCta: "Explorer les Partis ADA"
      },
      transformations: {
        title: "Les Trois Transformations Fondamentales",
        items: [
          {
            id: "institutional",
            title: "Transformation Institutionnelle",
            summary: "Reconstruire les institutions démocratiques avec transparence, responsabilité et participation citoyenne. Établir des cadres constitutionnels adaptés aux réalités africaines.",
            link: "/program/chapters#part-1"
          },
          {
            id: "economic",
            title: "Transformation Économique",
            summary: "Atteindre la souveraineté économique par la création de richesse territoriale, l'industrialisation stratégique et l'intégration continentale.",
            link: "/program/chapters#part-3"
          },
          {
            id: "social",
            title: "Transformation Sociale",
            summary: "Investir dans le capital humain par l'éducation, la santé et le renouveau culturel. Autonomiser la jeunesse et garantir un accès équitable aux opportunités.",
            link: "/program/chapters#part-5"
          }
        ]
      },
      continentalModel: {
        title: "Le Modèle Continental de l'ADA",
        description: "L'ADA fonctionne comme une alliance politique continentale composée de partis nationaux enregistrés partageant un manifeste, une charte et une vision institutionnelle communs."
      },
      architecture: {
        title: "Architecture du Programme",
        items: [
          {
            title: "Manifeste Continental",
            description: "La fondation idéologique partagée unissant tous les partis ADA à travers le continent."
          },
          {
            title: "Adaptation Nationale",
            description: "Traduction des principes continentaux pour répondre aux réalités et priorités locales."
          },
          {
            title: "Stratégie Parlementaire",
            description: "Construction d'un bloc ADA au Parlement Panafricain pour faire avancer la législation continentale."
          },
          {
            title: "Convergence Constitutionnelle",
            description: "Harmonisation à long terme des cadres constitutionnels vers l'union politique africaine."
          }
        ]
      },
      navigation: {
        house: {
          title: "La Maison de l'Alliance",
          subtitle: "Siège de Johannesburg",
          description: "Le centre politique, intellectuel et stratégique du mouvement continental."
        },
        cabinet: {
          title: "Cabinet Fantôme",
          subtitle: "Gouvernement en Attente",
          description: "Une structure exécutive symbolique modelant le futur gouvernement unifié de l'Afrique."
        }
      }
    },
    nav: {
      program: "Programme",
      overview: "Aperçu",
      brandedParties: "Partis ADA",
      house: "Maison de l'Alliance",
      shadowCabinet: "Cabinet Fantôme",
      chapters: "Chapitres du Programme"
    }
    // ... Additional French content follows same structure
  }
};

// Countries list for the application form
export const africanCountries = [
  "Algeria", "Angola", "Benin", "Botswana", "Burkina Faso", "Burundi",
  "Cameroon", "Cape Verde", "Central African Republic", "Chad", "Comoros",
  "Congo (Brazzaville)", "Congo (DRC)", "Côte d'Ivoire", "Djibouti", "Egypt",
  "Equatorial Guinea", "Eritrea", "Eswatini", "Ethiopia", "Gabon", "Gambia",
  "Ghana", "Guinea", "Guinea-Bissau", "Kenya", "Lesotho", "Liberia", "Libya",
  "Madagascar", "Malawi", "Mali", "Mauritania", "Mauritius", "Morocco",
  "Mozambique", "Namibia", "Niger", "Nigeria", "Rwanda", "São Tomé and Príncipe",
  "Senegal", "Seychelles", "Sierra Leone", "Somalia", "South Africa", "South Sudan",
  "Sudan", "Tanzania", "Togo", "Tunisia", "Uganda", "Zambia", "Zimbabwe"
];
